import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';



/* ---------- Icons ---------- */

const I = ({ d, className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ProfileIcon  = (p) => <I {...p} d="M12 4a4 4 0 100 8 4 4 0 000-8zM4 20a8 8 0 0116 0" />;
const BellIcon     = (p) => <I {...p} d="M12 3a5 5 0 00-5 5v3.5L5.5 15h13L17 11.5V8a5 5 0 00-5-5zM10 18a2 2 0 004 0" />;
const ShieldIcon   = (p) => <I {...p} d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z" />;
const ClockIcon    = (p) => <I {...p} d="M12 4a8 8 0 100 16 8 8 0 000-16zM12 8v4l3 2" />;

/* ---------- Reusable row pieces ---------- */

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm ${className}`}>
    {children}
  </div>
);

const SectionHead = ({ icon, title, subtitle }) => (
  <div className="px-5 pt-5 pb-4 flex items-center gap-3">
    <span className="w-9 h-9 rounded-lg bg-[#0b2740]/5 text-[#0b2740] flex items-center justify-center shrink-0">
      {icon}
    </span>
    <div>
      <div className="text-[15px] font-semibold text-[#1a2b3c]">{title}</div>
      {subtitle && <div className="text-[12px] text-gray-500 mt-0.5">{subtitle}</div>}
    </div>
  </div>
);

const LabelValue = ({ label, value }) => (
  <div className="px-5 py-4">
    <div className="text-[11px] tracking-[0.14em] uppercase text-gray-500">{label}</div>
    <div className="text-[14px] font-semibold text-[#1a2b3c] mt-1.5">{value || '—'}</div>
  </div>
);

const Toggle = ({ checked, onChange, label, description }) => (
  <div className="px-5 py-4 flex items-center gap-4">
    <div className="flex-1 min-w-0">
      <div className="text-[14px] font-semibold text-[#1a2b3c]">{label}</div>
      {description && (
        <div className="text-[12px] text-gray-500 mt-0.5">{description}</div>
      )}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
        checked ? 'bg-blue-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  </div>
);

/* ---------- Page ---------- */

const FintechProfile = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    securityAlerts: true,
    sessionTimeoutMinutes: 30,
  });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get('/fintech/dashboard');
        if (!alive) return;
        setData(res.data);
        setPrefs((p) => ({ ...p, ...(res.data.preferences || {}) }));

        // Keep drawer name in sync
        if (res.data.accountHolderName) {
          localStorage.setItem('fintech_name', res.data.accountHolderName);
          window.dispatchEvent(new Event('fintech-profile-updated'));
        }
        if (res.data.caseNumber) {
          localStorage.setItem('fintech_caseNumber', res.data.caseNumber);
          window.dispatchEvent(new Event('fintech-profile-updated'));
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/fintech/preferences', prefs);
      // Optional toast: import toast from 'react-hot-toast'; toast.success('Saved');
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading profile…</div>;
  if (!data) return <div className="p-6 text-sm text-red-600">Could not load your profile.</div>;

  return (
    <div className="px-4 pt-5 pb-8 lg:px-8 lg:pt-8 max-w-4xl mx-auto">
      {/* Title row */}
      <div className="mb-4 flex items-center gap-3">
        <span className="w-10 h-10 rounded-lg bg-[#0b2740] text-white flex items-center justify-center shrink-0">
          <ProfileIcon className="w-5 h-5" />
        </span>
        <div>
          <h1 className="font-serif text-[24px] leading-tight text-[#1a2b3c]">Profile</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">
            Your contact information and portal preferences.
          </p>
        </div>
      </div>

      {/* Personal information */}
      <Card className="mb-6">
        <SectionHead
          icon={<ProfileIcon className="w-4 h-4" />}
          title="Personal information"
        />
        <div className="border-t border-gray-100">
          <LabelValue label="Name" value={data.accountHolderName} />
          <div className="border-t border-gray-100" />
          <LabelValue label="Customer / reference number" value={data.caseNumber} />
          <div className="border-t border-gray-100" />
          <LabelValue label="Phone" value={data.phone} />
          <div className="border-t border-gray-100" />
          <LabelValue label="Email" value={data.email} />
          <div className="border-t border-gray-100" />
          <LabelValue label="Address" value={data.address} />
        </div>
      </Card>

      {/* Notifications */}
      <Card className="mb-6">
        <SectionHead
          icon={<BellIcon className="w-4 h-4" />}
          title="Notifications"
        />
        <div className="border-t border-gray-100">
          <Toggle
            label="Email notifications"
            description="Statements, alerts, and case updates by email."
            checked={prefs.emailNotifications}
            onChange={(v) => setPrefs((p) => ({ ...p, emailNotifications: v }))}
          />
          <div className="border-t border-gray-100" />
          <Toggle
            label="SMS notifications"
            description="Time-sensitive alerts by text message."
            checked={prefs.smsNotifications}
            onChange={(v) => setPrefs((p) => ({ ...p, smsNotifications: v }))}
          />
          <div className="border-t border-gray-100" />
          <Toggle
            label="Push notifications"
            description="Browser or device alerts when available."
            checked={prefs.pushNotifications}
            onChange={(v) => setPrefs((p) => ({ ...p, pushNotifications: v }))}
          />
        </div>
      </Card>

      {/* Security */}
      <Card className="mb-6">
        <SectionHead
          icon={<ShieldIcon className="w-4 h-4" />}
          title="Security"
        />
        <div className="border-t border-gray-100">
          <Toggle
            label="Sign-in & security alerts"
            description="Notify me about new sessions and important security events."
            checked={prefs.securityAlerts}
            onChange={(v) => setPrefs((p) => ({ ...p, securityAlerts: v }))}
          />
        </div>
      </Card>

      {/* Session timeout */}
      <Card className="mb-6">
        <SectionHead
          icon={<ClockIcon className="w-4 h-4" />}
          title="Session timeout"
        />
        <div className="border-t border-gray-100 px-5 py-4">
          <p className="text-[13px] text-gray-600">
            After this period of inactivity, you will be signed out automatically on this device.
          </p>
          <select
            value={prefs.sessionTimeoutMinutes}
            onChange={(e) =>
              setPrefs((p) => ({ ...p, sessionTimeoutMinutes: Number(e.target.value) }))
            }
            className="mt-3 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-[14px] text-[#1a2b3c] bg-white focus:outline-none focus:ring-2 focus:ring-[#0b2740]/30"
          >
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={60}>1 hour</option>
            <option value={120}>2 hours</option>
            <option value={240}>4 hours</option>
          </select>
        </div>
      </Card>

      {/* Save */}
      <button
        onClick={save}
        disabled={saving}
        className="inline-flex items-center justify-center bg-[#0b2740] hover:bg-[#12324f] text-white text-[13px] font-semibold px-5 py-3 rounded-lg transition-colors disabled:opacity-60"
      >
        {saving ? 'Saving…' : 'Save Changes'}
      </button>
    </div>
  );
};

export default FintechProfile;